import { FC } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Stack } from '@mui/system';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import WaveUIPageLoader from '@/shared/components/utilities/WaveUIPageLoader';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import ProductVariant from '@/shared/types/products/productVariant.type';
import Zone from '@/shared/types/shipping/zones/zone.type';

import useFetchShippingProfile from '../hooks/useFetchShippingProfile';
import useFetchShippingProviders from '../hooks/useFetchShippingProviders';
import useFetchTemplateSchemas from '../hooks/useFetchTemplateSchemas';
import AddZoneDialog from './components/AddZoneDialog/AddZoneDialog';
import AssignProductsDialog from './components/AssignProductsDialog';
import AssignedProductsAndVariantsTable from './components/AssignedProductsAndVariantsTable/AssignedProductsAndVariantsTable';
import ConfirmationRemoveAssignedVariantsDialog from './components/ConfirmationRemoveAssignedVariantsDialog';
import ConfirmationRemoveZonesDialog from './components/ConfirmationRemoveZonesDialog';
import CustomsDetailsPanel from './components/CustomsDetailsPanel';
import ErrorDialog from './components/ErrorDialog';
import ManageZonesTable_v2 from './components/ManageZonesTable_v2/ManageZonesTable_v2';
import ShippingProfileDetailsPanel from './components/ShippingProfileDetailsPanel/ShippingProfileDetailsPanel';
import Title from './components/Title';
import useEditShippingProfileOperations from './hooks/useEditShippingProfileOperations';

type DialogDataMap = {
  [DialogName.Error]: { title: string; message: string };
  [DialogName.AssignProducts]: null;
  [DialogName.ConfirmationRemoveAssignedVariants]: ProductVariant[];
  [DialogName.AddZone]: {
    key?: keyof Zone;
    zone?: Zone | Zone[];
  };
  [DialogName.ConfirmationRemoveZones]: {
    zones: Zone[];
  };
};

export type DialogDataType<T extends DialogName> = T extends keyof DialogDataMap
  ? DialogDataMap[T]
  : never;

const EditShippingProfile: FC = () => {
  const id = useParams().id || '';

  const { dialog, onUpdateDialogState } = useModalDialog<
    DialogDataType<DialogName>,
    DialogName
  >();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const {
    refreshTableIndex,
    isShippingProfileUpdating,
    updatedShippingProfile,
    handleAssignVariants,
    handleUpdateProfile,
    handleRemoveVariants,
    handleAddZone,
    handleUpdateZoneRate,
    handleRemoveZones,
  } = useEditShippingProfileOperations({
    profileId: id,
    onUpdateNotificationBar,
    onUpdateDialogState,
    onUpdateProgressDialog,
  });

  const { shippingProfile } = useFetchShippingProfile({
    profileId: id,
    updatedShippingProfile,
  });

  const { shippingProviders } = useFetchShippingProviders();
  const { templateSchemas } = useFetchTemplateSchemas();

  return (
    <div>
      <Title profileId={id} />

      {(!shippingProfile || !shippingProviders || !templateSchemas) && (
        <WaveUIPageLoader />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          alignItems: 'flex-start',
          gap: 2,
          mb: 4,
        }}
      >
        {shippingProfile && shippingProviders && templateSchemas && (
          <>
            <ShippingProfileDetailsPanel
              shippingProfile={shippingProfile}
              shippingProviders={shippingProviders}
              templateSchemas={templateSchemas}
              isLoading={isShippingProfileUpdating}
              onUpdateProfile={(profileDetails) => {
                void handleUpdateProfile(profileDetails);
              }}
            />

            <CustomsDetailsPanel
              templateSchemas={templateSchemas}
              isLoading={false}
              onUpdateCustomsDetails={(customsDetails) => {
                void handleUpdateProfile(customsDetails);
              }}
            />
          </>
        )}
      </Box>

      {shippingProfile && shippingProviders && templateSchemas && (
        <Stack spacing={2}>
          <AssignedProductsAndVariantsTable
            shippingProfile={shippingProfile}
            onAssignVariants={(assignedVariants) => {
              onUpdateDialogState(DialogName.AssignProducts, {
                open: true,
                data: assignedVariants,
                errors: [],
              });
            }}
            onRemoveAssignedVariants={(variants) => {
              onUpdateDialogState(
                DialogName.ConfirmationRemoveAssignedVariants,
                {
                  open: true,
                  data: variants,
                },
              );
            }}
          />

          <ManageZonesTable_v2
            refreshTableIndex={refreshTableIndex}
            shippingProfile={shippingProfile}
            onAddZone={(key, zone) => {
              onUpdateDialogState(DialogName.AddZone, {
                open: true,
                errors: [],
                data: {
                  key,
                  zone,
                },
              });
            }}
            onUpdateRate={(zone) => {
              onUpdateDialogState(DialogName.AddZone, {
                open: true,
                errors: [],
                data: {
                  key: 'shipping_rate',
                  zone,
                },
              });
            }}
            onRemoveZones={(zones) => {
              onUpdateDialogState(DialogName.ConfirmationRemoveZones, {
                open: true,
                errors: [],
                data: {
                  zones,
                },
              });
            }}
          />
        </Stack>
      )}

      {dialog?.error?.open && (
        <ErrorDialog
          onClose={() => {
            onUpdateDialogState(DialogName.Error, { open: false });
          }}
          errors={dialog.error.errors}
          title={(dialog.error.data as { title: string }).title}
          message={(dialog.error.data as { message: string }).message}
        />
      )}

      {dialog?.assignProducts?.open && (
        <AssignProductsDialog
          assignedVariants={dialog?.assignProducts?.data as ProductVariant[]}
          errors={dialog?.assignProducts?.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.AssignProducts, { open: false });
          }}
          proceed={(variants) => {
            void handleAssignVariants(variants);
          }}
        />
      )}

      {dialog?.confirmationRemoveAssignedVariants?.open && (
        <ConfirmationRemoveAssignedVariantsDialog
          selectedItemsLength={
            (
              dialog?.confirmationRemoveAssignedVariants
                ?.data as ProductVariant[]
            ).length
          }
          errors={dialog?.confirmationRemoveAssignedVariants?.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.ConfirmationRemoveAssignedVariants, {
              open: false,
            });
          }}
          proceed={() => {
            void handleRemoveVariants(
              dialog?.confirmationRemoveAssignedVariants
                ?.data as ProductVariant[],
            );
          }}
        />
      )}

      {dialog?.addZone?.open && templateSchemas && shippingProfile && (
        <AddZoneDialog
          zoneKey={(dialog?.addZone?.data as { key?: keyof Zone })?.key}
          zone={(dialog?.addZone?.data as { zone?: Zone })?.zone}
          errors={dialog?.addZone?.errors}
          templateSchemas={templateSchemas}
          shippingProfile={shippingProfile}
          onClose={() => {
            onUpdateDialogState(DialogName.AddZone, { open: false });
          }}
          proceed={(value, zoneKey) => {
            if (zoneKey === 'shipping_rate') {
              const zoneList = (dialog?.addZone?.data as { zone: Zone[] }).zone;

              void handleUpdateZoneRate(zoneList, {
                shipping_rate: value.shipping_rate,
                shipping_rate_type: value.shipping_rate_type,
              });
            } else {
              void handleAddZone(value);
            }
          }}
        />
      )}

      {dialog?.confirmationRemoveZones?.open && (
        <ConfirmationRemoveZonesDialog
          selectedItemsLength={
            (dialog?.confirmationRemoveZones?.data as { zones: Zone[] }).zones
              .length
          }
          errors={dialog?.confirmationRemoveZones?.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.ConfirmationRemoveZones, {
              open: false,
            });
          }}
          proceed={() => {
            const { zones } = dialog?.confirmationRemoveZones?.data as {
              zones: Zone[];
            };
            void handleRemoveZones(zones);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
      />
    </div>
  );
};

export default EditShippingProfile;
